import { atom } from 'jotai';

import { hasInternetConnectionAtom } from '@/_atoms/navigation';
import {
  isSavingRespondentAtom,
  hasUnsavedRespondentChangesAtom,
  respondentAtom,
  respondentErrorAtom
} from '@/_atoms/respondent';
import { handleErrorAtom } from '@/_atoms/handleError';
import { respondentPutQuery } from '@/_queries/respondentQueries';

import type { Respondent } from '@/types/respondent';

/**
 * Saves respondent information with single-flight and coalescing.
 *
 * Behavior:
 * - If a save loop is already running, do nothing (single-flight guard).
 * - While there are unsaved respondent changes:
 *   1. Send a PUT request to the API.
 *   2. Reset `hasUnsavedRespondentChangesAtom` to false.
 * - If an error occurs, record it in `respondentErrorAtom`, mark the changes as unsaved, and stop so they can be
 *   retried later.
 */
export const saveRespondentAtom = atom(
  null,
  async (get, set) => {
    // Record that there are unsaved changes
    set(hasUnsavedRespondentChangesAtom, true);

    // Single-flight guard. Prevent concurrent saves.
    if (get(isSavingRespondentAtom)) return;

    for (;;) {
      // Exit when there are no unsaved changes remaining
      if (!get(hasUnsavedRespondentChangesAtom)) return;

      const respondent: Respondent = get(respondentAtom);

      // Mark as saving and clear the unsaved flag (optimistically)
      set(isSavingRespondentAtom, true);
      set(hasUnsavedRespondentChangesAtom, false);

      try {
        await respondentPutQuery(respondent);
        set(hasInternetConnectionAtom, true);
      } catch (err) {
        // Record the error and mark changes as unsaved for retry
        set(handleErrorAtom, {
          error: err,
          errorAtom: respondentErrorAtom,
          consolePreamble: 'Error saving respondent:'
        });
        set(hasUnsavedRespondentChangesAtom, true);
        break;
      } finally {
        // Always clear saving state
        set(isSavingRespondentAtom, false);
      }
      // Loop continues: if unsaved changes are flagged during the save, process them next
    }
  }
);